
import { useState, useEffect } from 'react';
import Button from '../base/Button';

interface Folder {
  id: string;
  name: string;
  description: string;
  access_level: string;
  created_at: string;
  documents: { count: number }[];
}

interface FolderManagerProps {
  onFolderSelect?: (folderId: string | null) => void;
  selectedFolderId?: string | null;
  className?: string;
  userEmail: string;
  userAccessLevel: string;
}

export default function FolderManager({ 
  onFolderSelect, 
  selectedFolderId,
  className = '',
  userEmail,
  userAccessLevel
}: FolderManagerProps) {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [newFolder, setNewFolder] = useState({
    name: '',
    description: '',
    accessLevel: 'basic'
  });

  useEffect(() => {
    loadFolders();
  }, [userEmail]);

  const loadFolders = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/folder-permissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userEmail,
          action: 'get_user_folders'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to load folders');
      }

      const result = await response.json();
      setFolders(result.folders || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folders');
    } finally {
      setLoading(false);
    }
  };

  const createFolder = async () => {
    if (!newFolder.name.trim()) {
      setError('Folder name is required');
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/folder-permissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: userEmail,
          action: 'create_folder',
          folderData: {
            name: newFolder.name,
            description: newFolder.description,
            accessLevel: newFolder.accessLevel
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create folder');
      }

      setNewFolder({ name: '', description: '', accessLevel: 'basic' });
      setShowCreateForm(false);
      setError('');
      await loadFolders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    }
  };

  const updateFolder = async () => {
    if (!editingFolder || !editingFolder.name.trim()) {
      setError('Folder name is required');
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/folder-permissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: userEmail,
          action: 'update_folder',
          folderId: editingFolder.id,
          folderData: {
            name: editingFolder.name,
            description: editingFolder.description,
            accessLevel: editingFolder.access_level
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update folder');
      }

      setEditingFolder(null);
      setError('');
      await loadFolders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update folder');
    }
  };

  const deleteFolder = async (folderId: string) => {
    if (!confirm('Are you sure you want to delete this folder? All documents in this folder will also be deleted.')) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/folder-permissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userEmail,
          action: 'delete_folder',
          folderId: folderId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete folder');
      }

      if (selectedFolderId === folderId && onFolderSelect) {
        onFolderSelect(null);
      }
      
      await loadFolders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete folder');
    }
  };

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'public': return 'bg-green-100 text-green-800';
      case 'basic': return 'bg-blue-100 text-blue-800';
      case 'premium': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Document Folders</h3>
          {(userAccessLevel === 'master' || userAccessLevel === 'editor') && (
            <Button 
              size="sm" 
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              New Folder
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-100 border-l-4 border-red-500">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {showCreateForm && (userAccessLevel === 'master' || userAccessLevel === 'editor') && (
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Folder Name
              </label>
              <input
                type="text"
                value={newFolder.name}
                onChange={(e) => setNewFolder({ ...newFolder, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B1F3A]"
                placeholder="Enter folder name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={newFolder.description}
                onChange={(e) => setNewFolder({ ...newFolder, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B1F3A]"
                rows={2}
                placeholder="Optional description"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Access Level
              </label>
              <select
                value={newFolder.accessLevel}
                onChange={(e) => setNewFolder({ ...newFolder, accessLevel: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B1F3A] pr-8"
              >
                <option value="public">Public</option>
                <option value="basic">Basic</option>
                <option value="premium">Premium</option>
                <option value="admin">Admin Only</option>
              </select>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={createFolder} size="sm">
                Create Folder
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowCreateForm(false)}
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {editingFolder && (
        <div className="p-6 border-b border-gray-200 bg-blue-50">
          <h4 className="text-md font-medium text-gray-900 mb-4">Edit Folder</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Folder Name
              </label>
              <input
                type="text"
                value={editingFolder.name}
                onChange={(e) => setEditingFolder({ ...editingFolder, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B1F3A]"
                placeholder="Enter folder name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={editingFolder.description}
                onChange={(e) => setEditingFolder({ ...editingFolder, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B1F3A]"
                rows={2}
                placeholder="Optional description"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Access Level
              </label>
              <select
                value={editingFolder.access_level}
                onChange={(e) => setEditingFolder({ ...editingFolder, access_level: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B1F3A] pr-8"
              >
                <option value="public">Public</option>
                <option value="basic">Basic</option>
                <option value="premium">Premium</option>
                <option value="admin">Admin Only</option>
              </select>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={updateFolder} size="sm">
                Save Changes
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setEditingFolder(null)}
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="p-6">
        <div className="space-y-2">
          <button
            onClick={() => onFolderSelect && onFolderSelect(null)}
            className={`w-full text-left px-4 py-3 rounded-lg transition-colors cursor-pointer ${
              selectedFolderId === null 
                ? 'bg-[#9B1F3A] text-white' 
                : 'hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center">
              <i className="ri-folder-line mr-3"></i>
              <span className="font-medium">All Documents</span>
            </div>
          </button>

          {folders.map((folder) => (
            <div key={folder.id} className="group">
              <button
                onClick={() => onFolderSelect && onFolderSelect(folder.id)}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors cursor-pointer ${
                  selectedFolderId === folder.id 
                    ? 'bg-[#9B1F3A] text-white' 
                    : 'hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <i className="ri-folder-line mr-3"></i>
                    <div>
                      <div className="font-medium">{folder.name}</div>
                      {folder.description && (
                        <div className={`text-sm ${
                          selectedFolderId === folder.id ? 'text-white/80' : 'text-gray-500'
                        }`}>
                          {folder.description}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      selectedFolderId === folder.id 
                        ? 'bg-white/20 text-white' 
                        : getAccessLevelColor(folder.access_level)
                    }`}>
                      {folder.access_level}
                    </span>
                    
                    <span className={`text-sm ${
                      selectedFolderId === folder.id ? 'text-white/80' : 'text-gray-500'
                    }`}>
                      {folder.documents?.[0]?.count || 0} files
                    </span>
                    
                    {(userAccessLevel === 'master' || userAccessLevel === 'editor') && (
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingFolder(folder);
                          }}
                          className={`opacity-0 group-hover:opacity-100 p-1 rounded transition-opacity cursor-pointer ${
                            selectedFolderId === folder.id 
                              ? 'hover:bg-white/20 text-white' 
                              : 'hover:bg-blue-100 text-blue-600'
                          }`}
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteFolder(folder.id);
                          }}
                          className={`opacity-0 group-hover:opacity-100 p-1 rounded transition-opacity cursor-pointer ${
                            selectedFolderId === folder.id 
                              ? 'hover:bg-white/20 text-white' 
                              : 'hover:bg-red-100 text-red-600'
                          }`}
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
